import React, { useCallback, useEffect } from "react";
import { motion, useAnimationControls, useReducedMotion } from "motion/react";
import { Aster } from "@/components/brand/Aster";
import { useCompany } from "@/hooks/useCompany";
import { usePreferredName } from "@/hooks/usePreferredName";
import { useCurrentUser } from "@/auth/RequireAuth";
import { useSessionGreeting } from "./useSessionGreeting";
import { personalize } from "./greetings";

/** Dispatched when the user clicks "New Chat" so the welcome hero can spin the
 *  Aster even if it's already mounted (repeat clicks from the new-chat page). */
export const NEW_CHAT_EVENT = "artie:new-chat";

export function greetingForHour(h: number): string {
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/** New-chat hero (1a "Polished warm"): logo tile + 29px Hanken greeting.
 *  One layout for every preset — light and dark share the same type and
 *  structure; only the semantic token colors change per theme. */
export function EmptyChatWelcome() {
  const company = useCompany();
  const user = useCurrentUser();
  // Base line is chosen once per login (stable across new chats); the name
  // fills in once the profile settles, so the heading renders once, not twice.
  const base = useSessionGreeting(user?.id);
  const { name, ready } = usePreferredName();
  const reduce = useReducedMotion() ?? false;
  const controls = useAnimationControls();

  const spin = useCallback(() => {
    if (reduce) return;
    controls.set({ rotate: 0 });
    void controls.start({ rotate: 360, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } });
  }, [controls, reduce]);

  // Spin when the hero first appears (arriving at a new chat)…
  useEffect(() => {
    spin();
  }, [spin]);
  // …and every time "New Chat" is clicked, even when this view is already
  // mounted (clicking New Chat while already on the new-chat page).
  useEffect(() => {
    const onNewChat = () => spin();
    window.addEventListener(NEW_CHAT_EVENT, onNewChat);
    return () => window.removeEventListener(NEW_CHAT_EVENT, onNewChat);
  }, [spin]);

  return (
    <div className="flex w-full flex-col items-center text-center font-sans motion-safe:animate-[lg-rise_280ms_cubic-bezier(0.16,1,0.3,1)]">
      {/* Aster spins on mount / New Chat, and is also clickable to spin again. */}
      <motion.button
        type="button"
        onClick={spin}
        aria-label="Artie"
        className="mb-6 grid cursor-pointer place-items-center border-0 bg-transparent p-0"
        initial={false}
        animate={controls}
      >
        <Aster variant="primary" size={56} />
      </motion.button>
      {/* Reserve the line height while the name settles, then fade the final
          greeting in — so it appears once rather than visibly rewriting. */}
      <h1 className="m-0 text-[29px] font-bold tracking-[-0.01em] text-ink">
        <span className={ready ? "opacity-100 transition-opacity duration-300" : "opacity-0"}>
          {ready ? personalize(base, name) : " "}
        </span>
      </h1>
      {/* <p className="mt-2 mb-0 text-[15px] text-ink-secondary">
        {company ? (
          <>Ask about anything at <b className="font-semibold text-accent dark:text-green-bright">{company.name}</b>, or pick a starting point.</>
        ) : (
          <>Ask about anything, or pick a starting point.</>
        )}
      </p> */}
    </div>
  );
}
